import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * 控制 SessionStorage 读写操作的选项接口
 */
interface MediaNameSyncOptions {
  /**
   * 是否可以从 SessionStorage 读取值
   * @default true
   */
  canRead?: boolean;

  /**
   * 是否可以写入值到 SessionStorage
   * @default false
   */
  canWrite?: boolean;

  /**
   * 读取后是否清除 SessionStorage 中的值
   * @default false
   */
  removeAfterRead?: boolean;
}

/**
 * 自定义 Hook，用于同步 SessionStorage 中的 initial_media_name 与输入框状态
 * @param initialValue - 输入框的初始默认值
 * @param options - 控制 SessionStorage 读写操作的选项
 * @returns [值, 设置值的函数, 手动写入存储的函数]
 */
export const useMediaNameSync = (initialValue: string = '', options: MediaNameSyncOptions = {}) => {
  const STORAGE_KEY = 'initial_media_name';

  // 设置选项默认值
  const { canRead = true, canWrite = false, removeAfterRead = false } = options;

  // 跟踪是否已从storage中读取过值
  const hasReadRef = useRef(false);

  // 使用默认值初始化状态
  const [value, setValue] = useState(initialValue);

  // 组件挂载时读取sessionStorage
  useEffect(() => {
    // 如果不可读或已经读取过，则跳过
    if (!canRead || typeof window === 'undefined' || hasReadRef.current) {
      return;
    }

    const storedValue = sessionStorage.getItem(STORAGE_KEY);

    // 如果找到了值，更新状态
    if (storedValue) {
      setValue(storedValue);

      // 如果设置了读取后移除，删除存储的值
      if (removeAfterRead) {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }

    // 标记为已读取
    hasReadRef.current = true;
  }, [canRead, removeAfterRead]);

  // 更新值的函数
  const handleValueChange = useCallback(
    (newValue: string) => {
      setValue(newValue);

      if (typeof window !== 'undefined' && canWrite) {
        if (newValue) {
          sessionStorage.setItem(STORAGE_KEY, newValue);
        } else {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
    },
    [canWrite]
  );

  // 手动写入到 SessionStorage 的函数
  const writeToStorage = useCallback(
    (valueToWrite: string = value) => {
      if (typeof window !== 'undefined') {
        if (valueToWrite) {
          sessionStorage.setItem(STORAGE_KEY, valueToWrite);
        } else {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
    },
    [value]
  );

  return [value, handleValueChange, writeToStorage] as const;
};

export default useMediaNameSync;
